import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useRecordVisit } from '@/features/app-shell/hooks/useRecordVisit';
import { AlertTriangle, ArrowLeft, Ban, Loader2, RefreshCw, Send } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogClose,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { MisLink } from '@/components/MisLink';
import { PermissionGuard } from '@/components/PermissionGuard';
import { PERMISSIONS } from '@features/shared/constants/permissions';
import { useMaterialRequestStore } from '../store';
import { Textarea } from '@/components/ui/textarea';
import { materialRequestService } from '../services';
import { DocumentStatus, type MaterialRequestItemDto } from '../types';
import { MrsDocumentStatusBadge } from '../components/IssuanceStatusBadge';
import { PanelSkeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function formatDateTime(dateString: string): string {
  return new Date(dateString).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const documentStatusConfig: Record<DocumentStatus, { label: string; variant: 'default' | 'secondary' | 'outline' | 'destructive' }> = {
  [DocumentStatus.Draft]: { label: 'Draft', variant: 'secondary' },
  [DocumentStatus.Open]: { label: 'Open', variant: 'default' },
  [DocumentStatus.Closed]: { label: 'Closed', variant: 'outline' },
  [DocumentStatus.Cancelled]: { label: 'Cancelled', variant: 'destructive' },
};

function ItemProgressBar({ item }: { item: MaterialRequestItemDto }) {
  const requested = item.requestedQuantity;
  const released = item.releasedQuantity;
  const inPicklists = item.pendingQuantityInPicklists;
  const available = item.availableQuantity;

  const releasedPct = requested > 0 ? Math.min(100, (released / requested) * 100) : 0;
  const picklistPct = requested > 0
    ? Math.min(100 - releasedPct, (inPicklists / requested) * 100)
    : 0;

  const caption =
    inPicklists > 0
      ? `${released} released · ${inPicklists} in picklists / ${requested}`
      : released >= requested && requested > 0
        ? `${requested} / ${requested} released`
        : `${released} / ${requested} released`;

  const breakdown = [
    { label: 'Released (posted)', value: released, dot: 'bg-success' },
    { label: 'In picklists', value: inPicklists, dot: 'bg-warning' },
    { label: 'Available', value: available, dot: 'bg-muted-foreground/30' },
  ];

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="group w-full cursor-help space-y-1">
            <div className="flex h-2 w-full overflow-hidden rounded-full bg-border ring-offset-background transition-all group-hover:ring-2 group-hover:ring-ring/40 group-hover:ring-offset-1">
              {releasedPct > 0 && (
                <div className="h-full bg-success transition-all" style={{ width: `${releasedPct}%` }} />
              )}
              {picklistPct > 0 && (
                <div className="h-full bg-warning transition-all" style={{ width: `${picklistPct}%` }} />
              )}
            </div>
            <p className="text-xs text-muted-foreground">{caption}</p>
          </div>
        </TooltipTrigger>
        <TooltipContent side="top" align="end" className="w-48">
          <p className="mb-1.5 font-mono text-xs font-medium">{item.itemCode}</p>
          <ul className="space-y-1">
            {breakdown.map((row) => (
              <li key={row.label} className="flex items-center justify-between gap-3 text-xs">
                <span className="flex items-center gap-1.5 text-muted-foreground">
                  <span className={cn('h-2 w-2 rounded-full', row.dot)} />
                  {row.label}
                </span>
                <span className="font-medium tabular-nums">{row.value.toLocaleString()}</span>
              </li>
            ))}
            <li className="mt-1 flex items-center justify-between gap-3 border-t pt-1 text-xs">
              <span className="text-muted-foreground">Requested</span>
              <span className="font-medium tabular-nums">{requested.toLocaleString()}</span>
            </li>
          </ul>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}


export function MaterialRequestDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const canGoBack = (window.history.state?.idx ?? 0) > 0;
  const goBack = () => canGoBack ? navigate(-1) : navigate('/mro/issuance/mrs');

  const {
    selectedRequestDetail,
    isLoadingDetail,
    detailError,
    fetchRequestById,
    clearSelectedRequestDetail,
  } = useMaterialRequestStore();

  const [isServing, setIsServing] = useState(false);
  const [showServeDialog, setShowServeDialog] = useState(false);
  const [serveReason, setServeReason] = useState('');
  const [isCancelling, setIsCancelling] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [isResyncing, setIsResyncing] = useState(false);

  usePageTitle(selectedRequestDetail ? `MRS ${selectedRequestDetail.mrsNumber}` : 'Material Request');

  // M4 (86d44z223) — Recent. On RESOLVE, and before this component's early returns: hooks cannot
  // live below the `if (!selectedRequestDetail)` guard further down.
  useRecordVisit('mrs', selectedRequestDetail?.id, selectedRequestDetail?.mrsNumber);

  useEffect(() => {
    if (id) {
      fetchRequestById(Number(id));
    }
    return () => {
      clearSelectedRequestDetail();
    };
  }, [id, fetchRequestById, clearSelectedRequestDetail]);

  if (isLoadingDetail) {
    return (
      <div className="space-y-6">
        <PanelSkeleton rows={6} />
        <PanelSkeleton rows={3} />
        <PanelSkeleton rows={4} />
      </div>
    );
  }

  if (detailError) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <p className="text-destructive">{detailError}</p>
        <Button variant="outline" onClick={() => id && fetchRequestById(Number(id))}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Retry
        </Button>
        <Button variant="ghost" onClick={() => goBack()}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Requests
        </Button>
      </div>
    );
  }

  if (!selectedRequestDetail) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <p className="text-muted-foreground">Material request not found</p>
        <Button variant="outline" onClick={() => goBack()}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Requests
        </Button>
      </div>
    );
  }

  const mr = selectedRequestDetail;

  const isFullyFulfilled = mr.items.every((i) => i.releasedQuantity >= i.requestedQuantity);
  const showServeButton = mr.documentStatus === DocumentStatus.Open;
  const showCancelButton = mr.documentStatus === DocumentStatus.Open;

  const handleServe = async () => {
    setIsServing(true);
    try {
      await materialRequestService.serveMrs(mr.id, serveReason || undefined);
      toast.success('MRS served successfully');
      setShowServeDialog(false);
      setServeReason('');
      fetchRequestById(mr.id);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to serve MRS');
    } finally {
      setIsServing(false);
    }
  };

  const handleCancel = async () => {
    setIsCancelling(true);
    try {
      await materialRequestService.cancelMrs(mr.id, cancelReason || undefined);
      toast.success('MRS cancelled successfully');
      setShowCancelDialog(false);
      setCancelReason('');
      fetchRequestById(mr.id);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to cancel MRS');
    } finally {
      setIsCancelling(false);
    }
  };

  const handleResync = async () => {
    setIsResyncing(true);
    try {
      const result = await materialRequestService.resyncMrs(mr.id);
      if (result.errors.length > 0) {
        toast.warning(result.errors[0]);
      } else {
        toast.success('MRS re-synced from NetSuite successfully');
      }
      fetchRequestById(mr.id);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to resync MRS');
    } finally {
      setIsResyncing(false);
    }
  };

  // Fulfillment summary
  const totalRequested = mr.items.reduce((sum, i) => sum + i.requestedQuantity, 0);
  const totalFulfilled = mr.items.reduce((sum, i) => sum + i.releasedQuantity, 0);
  const fullyFulfilledCount = mr.items.filter((i) => i.releasedQuantity >= i.requestedQuantity).length;
  const pendingCount = mr.items.filter((i) => i.releasedQuantity < i.requestedQuantity).length;
  const overallPct = totalRequested > 0
    ? (totalFulfilled >= totalRequested ? 100 : Math.floor((totalFulfilled / totalRequested) * 100))
    : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => goBack()}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex items-center gap-3">
            <div>
              <h1 className="text-2xl font-bold font-mono">{mr.mrsNumber}</h1>
              <p className="text-muted-foreground text-sm mt-1">
                Synced {formatDateTime(mr.createdAt)}
                {mr.closedAt && ` · Served ${formatDateTime(mr.closedAt)}`}
                {mr.cancelledAt && ` · Cancelled ${formatDateTime(mr.cancelledAt)}`}
              </p>
            </div>
            <MrsDocumentStatusBadge status={mr.documentStatus} />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <PermissionGuard permission={PERMISSIONS.ISSUANCE_SYNC_REQUESTS}>
            <Button variant="outline" size="sm" onClick={handleResync} disabled={isResyncing}>
              {isResyncing
                ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                : <RefreshCw className="mr-2 h-4 w-4" />}
              Resync from NetSuite
            </Button>
          </PermissionGuard>
          {showCancelButton && (
            <PermissionGuard permission={PERMISSIONS.ISSUANCE_POST_PICKLIST}>
              <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
                <DialogTrigger asChild>
                  <Button variant="outline" size="sm">
                    <Ban className="mr-2 h-4 w-4" />
                    Cancel MRS
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Cancel MRS</DialogTitle>
                    <DialogDescription>
                      This will cancel the material request and update NetSuite. This action cannot be undone.
                    </DialogDescription>
                  </DialogHeader>
                  <Textarea
                    placeholder="Reason for cancellation (optional)"
                    value={cancelReason}
                    onChange={(e) => setCancelReason(e.target.value)}
                    rows={3}
                  />
                  <DialogFooter>
                    <DialogClose asChild>
                      <Button variant="outline" disabled={isCancelling}>Close</Button>
                    </DialogClose>
                    <Button variant="destructive" onClick={handleCancel} disabled={isCancelling}>
                      {isCancelling && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      Confirm Cancel
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </PermissionGuard>
          )}
          {showServeButton && (
            <PermissionGuard permission={PERMISSIONS.ISSUANCE_POST_PICKLIST}>
              <Dialog open={showServeDialog} onOpenChange={setShowServeDialog}>
                <DialogTrigger asChild>
                  <Button size="sm">
                    <Send className="mr-2 h-4 w-4" />
                    Serve
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Serve MRS</DialogTitle>
                    <DialogDescription>
                      {isFullyFulfilled
                        ? 'All items are fully fulfilled. This will mark the MRS as Served on NetSuite.'
                        : 'Not all items are fully fulfilled. A reason is required for partial serve.'}
                    </DialogDescription>
                  </DialogHeader>
                  {!isFullyFulfilled && (
                    <Textarea
                      placeholder="Reason for partial serve (required)"
                      value={serveReason}
                      onChange={(e) => setServeReason(e.target.value)}
                      rows={3}
                    />
                  )}
                  <DialogFooter>
                    <DialogClose asChild>
                      <Button variant="outline" disabled={isServing}>Close</Button>
                    </DialogClose>
                    <Button
                      onClick={handleServe}
                      disabled={isServing || (!isFullyFulfilled && !serveReason.trim())}
                    >
                      {isServing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      Confirm Serve
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </PermissionGuard>
          )}
        </div>
      </div>

      {/* ForAdjustment Alert */}
      {mr.forAdjustment && (
        <div className="flex items-start gap-3 rounded-lg border border-warning/30 bg-warning/10 p-4">
          <AlertTriangle className="h-5 w-5 text-warning mt-0.5 shrink-0" />
          <div className="space-y-1">
            <p className="font-medium text-warning">
              Flagged for Adjustment
            </p>
            <p className="text-sm text-warning">
              This MRS no longer meets the required status conditions in NetSuite (MRO/PR status may have reverted).
              It has been excluded from picklist creation. Use "Resync from NetSuite" to re-check the status.
            </p>
          </div>
        </div>
      )}

      {/* Request Summary Card */}
      <Card>
        <CardHeader>
          <CardTitle>Request Summary</CardTitle>
          <CardDescription>NetSuite ID: {mr.netsuiteIdNumber}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-x-8 gap-y-3 text-sm md:grid-cols-3">
            <div>
              <p className="text-muted-foreground">Requestor</p>
              <p className="font-medium">{mr.requestorFullName || '-'}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Employee ID</p>
              <p className="font-medium">{mr.requestorIdNumber || '-'}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Approved By</p>
              <p className="font-medium">{mr.approvedBy || '-'}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Requested Date</p>
              <p className="font-medium">{formatDate(mr.requestedAt)}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Last Updated in NetSuite</p>
              <p className="font-medium">{formatDateTime(mr.netsuiteUpdatedAt)}</p>
            </div>
            {mr.items[0]?.netsuiteDepartmentName && (
              <div>
                <p className="text-muted-foreground">Department / Cost Center</p>
                <p className="font-medium">{mr.items[0].netsuiteDepartmentName}</p>
              </div>
            )}
            {mr.items[0]?.netsuiteEndUseName && (
              <div>
                <p className="text-muted-foreground">End Use</p>
                <p className="font-medium">{mr.items[0].netsuiteEndUseName}</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Fulfillment Progress */}
      <Card>
        <CardHeader>
          <CardTitle>Fulfillment Progress</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">
                {totalFulfilled.toLocaleString()} of {totalRequested.toLocaleString()} units fulfilled
              </span>
              <span className="font-medium">{overallPct}%</span>
            </div>
            <div className="w-full h-2.5 bg-border rounded-full overflow-hidden">
              <div
                className="h-full bg-success transition-all"
                style={{ width: `${overallPct}%` }}
              />
            </div>
          </div>
          <div className="flex gap-6 text-sm">
            <div>
              <span className="text-muted-foreground">Total Items:</span>
              <span className="ml-2 font-medium">{mr.items.length}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Fully Fulfilled:</span>
              <span className="ml-2 font-medium text-success">{fullyFulfilledCount}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Still Pending:</span>
              <span className="ml-2 font-medium text-warning">{pendingCount}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Items Table */}
      <Card>
        <CardHeader>
          <CardTitle>Items</CardTitle>
          <CardDescription>{mr.items.length} item{mr.items.length !== 1 ? 's' : ''}</CardDescription>
        </CardHeader>
        <CardContent className="px-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="pl-6">Item Code</TableHead>
                <TableHead>Item Name</TableHead>
                <TableHead>Remarks</TableHead>
                <TableHead>UOM</TableHead>
                <TableHead className="text-right">Requested</TableHead>
                <TableHead className="text-right">Released</TableHead>
                <TableHead className="text-right">In Picklists</TableHead>
                <TableHead className="pr-6 w-64">Progress</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {mr.items.map((item) => {
                const isFulfilled = item.releasedQuantity >= item.requestedQuantity;
                const isPartial = !isFulfilled && (item.releasedQuantity > 0 || item.pendingQuantityInPicklists > 0);
                return (
                  <TableRow
                    key={item.id}
                    className={cn(
                      isFulfilled && 'bg-success/5',
                      isPartial && 'bg-warning/5',
                    )}
                  >
                    <TableCell className="pl-6 font-mono text-sm">{item.itemCode}</TableCell>
                    <TableCell className="max-w-[240px]">
                      <p className="font-medium truncate">{item.itemName}</p>
                      {item.netsuiteItemClass && (
                        <p className="text-xs text-muted-foreground truncate">
                          {item.netsuiteItemSubClass
                            ? `${item.netsuiteItemClass} / ${item.netsuiteItemSubClass}`
                            : item.netsuiteItemClass}
                        </p>
                      )}
                    </TableCell>
                    <TableCell className="max-w-[250px]">
                      {item.remarks ? (
                        <Dialog>
                          <DialogTrigger asChild>
                            <p className="text-sm text-muted-foreground line-clamp-3 cursor-pointer hover:text-foreground transition-colors">
                              {item.remarks}
                            </p>
                          </DialogTrigger>
                          <DialogContent className="max-w-lg">
                            <DialogHeader>
                              <DialogTitle>Item Remarks</DialogTitle>
                              {/* 86d48d39v: ItemName is uncapped in the DB — clamp the subtitle */}
                              <DialogDescription className="line-clamp-2">{item.itemCode} &mdash; {item.itemName}</DialogDescription>
                            </DialogHeader>
                            <p className="text-sm whitespace-pre-wrap max-h-[60vh] overflow-y-auto">{item.remarks}</p>
                          </DialogContent>
                        </Dialog>
                      ) : (
                        <span className="text-sm text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>{item.unitOfMeasure}</TableCell>
                    <TableCell className="text-right">{item.requestedQuantity.toLocaleString()}</TableCell>
                    <TableCell className="text-right">{item.releasedQuantity.toLocaleString()}</TableCell>
                    <TableCell className="text-right">{item.pendingQuantityInPicklists.toLocaleString()}</TableCell>
                    <TableCell className="pr-6">
                      <ItemProgressBar item={item} />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Linked Picklists */}
      <Card>
        <CardHeader>
          <CardTitle>Linked Picklists</CardTitle>
          <CardDescription>
            Picklists created against this material request
          </CardDescription>
        </CardHeader>
        <CardContent className="px-0">
          {mr.linkedPicklists.length === 0 ? (
            <div className="flex items-center justify-center py-10 text-muted-foreground text-sm">
              No picklists have been created for this request yet.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="pl-6">Picklist Number</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Items from this MRS</TableHead>
                  <TableHead>Pickers</TableHead>
                  <TableHead>MIS Number</TableHead>
                  <TableHead className="pr-6">Created</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {mr.linkedPicklists.map((pl) => {
                  const statusCfg = documentStatusConfig[pl.documentStatus] ?? { label: 'Unknown', variant: 'outline' as const };
                  return (
                    <TableRow key={pl.id}>
                      <TableCell className="pl-6">
                        <Link
                          to={`/mro/issuance/picklist/${pl.id}`}
                          className="font-mono text-sm font-medium text-primary hover:underline"
                        >
                          {pl.picklistNumber}
                        </Link>
                      </TableCell>
                      <TableCell>
                        <Badge variant={statusCfg.variant}>{statusCfg.label}</Badge>
                      </TableCell>
                      <TableCell className="text-right">{pl.itemCountForThisMrs}</TableCell>
                      <TableCell>
                        {pl.pickerNames.length > 0
                          ? pl.pickerNames.join(', ')
                          : <span className="text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell>
                        {(pl.misReferences ?? []).length > 0
                          ? (
                            <div className="flex flex-col gap-0.5">
                              {(pl.misReferences ?? []).map((misRef) => (
                                <MisLink key={misRef.misId} misId={misRef.misId} misTranId={misRef.misTranId} url={misRef.url} className="text-sm" />
                              ))}
                            </div>
                          )
                          : <span className="text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell className="pr-6 text-sm text-muted-foreground">
                        {formatDate(pl.createdAt)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
